import { useState, useEffect } from "react";
import { AudioButton } from "./AudioButton.js";
import { getTextsForLanguage, type ComprehensionText } from "../content/comprehensionTexts.js";
import { recordSkillBatch } from "../utils/progress.js";

const C = {
  bg: 'var(--bg)', surf: 'var(--surf)', surf2: 'var(--surf2)',
  border: 'var(--border)', text: 'var(--text)', muted: 'var(--muted)', dim: 'var(--dim)',
  accentBg: 'var(--accent-bg)', accentBorder: 'var(--accent-border)',
  green: 'var(--success)', greenBg: 'var(--success-bg)', greenBorder: 'var(--success-border)',
  red: 'var(--error)', redBg: 'var(--error-bg)', redBorder: 'var(--error-border)',
};
const AF = {
  h: 'var(--font-h, "Space Grotesk", system-ui, sans-serif)',
  b: "'DM Sans', system-ui, sans-serif",
  m: "'DM Mono', 'Courier New', monospace",
};

const LEVEL_STYLE: Record<string, { color: string; bg: string; border: string }> = {
  A1: { color: 'var(--success)',       bg: 'var(--success-bg)',  border: 'var(--success-border)' },
  A2: { color: 'var(--blue)',          bg: 'var(--blue-bg)',     border: 'var(--blue-bg)'        },
  B1: { color: 'var(--warn)',          bg: 'var(--warn-bg)',     border: 'var(--warn-border)'    },
  B2: { color: 'var(--accent)',        bg: 'var(--accent-bg)',   border: 'var(--accent-border)'  },
};

type CompletionRecord = { score: number; total: number; completedAt: number };
type View = "list" | "reading" | "quiz" | "results";

function loadCompletions(): Record<string, CompletionRecord> {
  if (typeof window === "undefined") return {};
  try { return JSON.parse(localStorage.getItem("conjugr8.comprehension") || "{}"); }
  catch { return {}; }
}

function saveCompletion(id: string, record: CompletionRecord) {
  if (typeof window === "undefined") return;
  try {
    const all = loadCompletions();
    all[id] = record;
    localStorage.setItem("conjugr8.comprehension", JSON.stringify(all));
  } catch {}
}

function LevelBadge({ level }: { level: string }) {
  const s = LEVEL_STYLE[level] ?? LEVEL_STYLE.A1;
  return (
    <span style={{
      fontFamily: AF.m, fontSize: 10, fontWeight: 700, letterSpacing: '0.05em',
      padding: '3px 8px', borderRadius: 20,
      color: s.color, background: s.bg, border: `1px solid ${s.border}`,
    }}>{level}</span>
  );
}

export function ReadingExercise({ languageCode }: { languageCode: string }) {
  const [view, setView] = useState<View>("list");
  const [selectedText, setSelectedText] = useState<ComprehensionText | null>(null);
  const [levelFilter, setLevelFilter] = useState<"all" | "A1" | "A2" | "B1" | "B2">("all");
  const [answers, setAnswers] = useState<(number | null)[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [completions, setCompletions] = useState<Record<string, CompletionRecord>>({});

  useEffect(() => { setCompletions(loadCompletions()); }, []);

  const texts = getTextsForLanguage(languageCode);
  const filtered = levelFilter === "all" ? texts : texts.filter(t => t.level === levelFilter);
  const availableLevels = (["A1","A2","B1","B2"] as const).filter(l => texts.some(t => t.level === l));
  const totalCompleted = texts.filter(t => completions[t.id]).length;
  const allAnswered = selectedText ? answers.every(a => a !== null) : false;

  function openText(text: ComprehensionText) {
    setSelectedText(text);
    setAnswers(new Array(text.questions.length).fill(null));
    setSubmitted(false);
    setView("reading");
  }

  function selectAnswer(qi: number, oi: number) {
    if (submitted) return;
    setAnswers(prev => { const n = [...prev]; n[qi] = oi; return n; });
  }

  function submitQuiz() {
    if (!selectedText || !allAnswered) return;
    setSubmitted(true);
    const correct = answers.filter((a, i) => a === selectedText.questions[i].correctIndex).length;
    const record: CompletionRecord = { score: correct, total: selectedText.questions.length, completedAt: Date.now() };
    recordSkillBatch("reading", correct, selectedText.questions.length);
    saveCompletion(selectedText.id, record);
    setCompletions(prev => ({ ...prev, [selectedText.id]: record }));
    setView("results");
  }

  function backToList() { setView("list"); setSelectedText(null); }

  // ── LIST ──────────────────────────────────────────────────────────────────
  if (view === "list") return (
    <div>
      {totalCompleted > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 16px', borderRadius: 8, marginBottom: 20,
          background: C.greenBg, border: `1px solid ${C.greenBorder}`,
        }}>
          <span style={{ fontFamily: AF.m, fontSize: 12, color: C.green }}>
            ✓ {totalCompleted} / {texts.length} passages completed
          </span>
        </div>
      )}

      {/* Level filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {(['all', ...availableLevels] as const).map(l => {
          const isActive = levelFilter === l;
          return (
            <button key={l} onClick={() => setLevelFilter(l)} style={{
              fontFamily: AF.b, fontSize: 12, fontWeight: isActive ? 700 : 400,
              padding: '6px 14px', borderRadius: 20, cursor: 'pointer',
              border: `1px solid ${isActive ? C.text : C.border}`,
              background: isActive ? C.text : 'transparent',
              color: isActive ? C.bg : C.muted,
            }}>
              {l === 'all' ? 'All levels' : l}
            </button>
          );
        })}
      </div>

      {/* Passage grid */}
      {filtered.length === 0 ? (
        <p style={{ fontFamily: AF.b, fontSize: 14, color: C.muted }}>No passages for this level yet.</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
          {filtered.map(text => {
            const done = completions[text.id];
            return (
              <button key={text.id} onClick={() => openText(text)} style={{
                textAlign: 'left', padding: '16px 18px', borderRadius: 12,
                border: `1px solid ${C.border}`, background: C.surf,
                cursor: 'pointer', transition: 'border-color 0.15s',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <LevelBadge level={text.level}/>
                  {done ? (
                    <span style={{ fontFamily: AF.m, fontSize: 11, color: C.green }}>✓ {done.score}/{done.total}</span>
                  ) : (
                    <span style={{ fontFamily: AF.m, fontSize: 11, color: C.dim }}>New</span>
                  )}
                </div>
                <div style={{ fontFamily: AF.h, fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 6, letterSpacing: '-0.2px' }}>
                  {text.title}
                </div>
                <div style={{ fontFamily: AF.b, fontSize: 12, color: C.muted }}>
                  {text.wordCount} words · {text.questions.length} questions
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  if (!selectedText) return null;

  // ── READING ───────────────────────────────────────────────────────────────
  if (view === "reading") return (
    <div style={{ maxWidth: 640 }}>
      <button onClick={backToList} style={{
        fontFamily: AF.b, fontSize: 13, color: C.muted, background: 'transparent',
        border: 'none', cursor: 'pointer', padding: 0, marginBottom: 20,
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        ← Back to passages
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <LevelBadge level={selectedText.level}/>
        <span style={{ fontFamily: AF.m, fontSize: 11, color: C.dim }}>{selectedText.wordCount} words</span>
      </div>

      <h2 style={{ fontFamily: AF.h, fontSize: 22, fontWeight: 700, color: C.text, margin: '0 0 16px', letterSpacing: '-0.6px' }}>
        {selectedText.title}
      </h2>

      <div style={{ background: C.surf, border: `1px solid ${C.border}`, borderRadius: 12, padding: '22px 24px', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <p style={{ fontFamily: AF.b, fontSize: 16, lineHeight: 1.7, color: C.text, margin: 0, flex: 1 }}>
            {selectedText.text}
          </p>
          <div style={{ flexShrink: 0 }}>
            <AudioButton text={selectedText.text} size="md"/>
          </div>
        </div>
      </div>

      <button onClick={() => setView("quiz")} style={{
        fontFamily: AF.b, fontSize: 14, fontWeight: 700,
        background: 'var(--accent)', color: C.bg, border: 'none',
        padding: '13px 28px', borderRadius: 9, cursor: 'pointer',
      }}>
        Answer questions ({selectedText.questions.length}) →
      </button>
    </div>
  );

  // ── QUIZ ──────────────────────────────────────────────────────────────────
  if (view === "quiz") return (
    <div style={{ maxWidth: 640 }}>
      <button onClick={() => setView("reading")} style={{
        fontFamily: AF.b, fontSize: 13, color: C.muted, background: 'transparent',
        border: 'none', cursor: 'pointer', padding: 0, marginBottom: 20,
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        ← Re-read passage
      </button>

      <h2 style={{ fontFamily: AF.h, fontSize: 20, fontWeight: 700, color: C.text, margin: '0 0 4px', letterSpacing: '-0.5px' }}>
        {selectedText.title}
      </h2>
      <p style={{ fontFamily: AF.b, fontSize: 13, color: C.muted, margin: '0 0 24px' }}>
        Answer all questions, then submit.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 24 }}>
        {selectedText.questions.map((q, qi) => (
          <div key={qi} style={{ background: C.surf, border: `1px solid ${C.border}`, borderRadius: 12, padding: '18px 20px' }}>
            <p style={{ fontFamily: AF.b, fontSize: 14, fontWeight: 600, color: C.text, margin: '0 0 14px' }}>
              {qi + 1}. {q.question}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {q.options.map((opt, oi) => {
                const isSelected = answers[qi] === oi;
                return (
                  <button key={oi} onClick={() => selectAnswer(qi, oi)} style={{
                    textAlign: 'left', padding: '11px 14px', borderRadius: 8, cursor: 'pointer',
                    fontFamily: AF.b, fontSize: 13, color: isSelected ? 'var(--accent)' : C.text,
                    border: `1px solid ${isSelected ? 'var(--accent)' : C.border}`,
                    background: isSelected ? C.accentBg : 'transparent',
                    transition: 'all 0.12s',
                    display: 'flex', alignItems: 'center', gap: 10,
                  }}>
                    <span style={{ fontFamily: AF.m, fontSize: 11, color: isSelected ? 'var(--accent)' : C.dim, flexShrink: 0 }}>
                      {String.fromCharCode(65 + oi)}
                    </span>
                    {opt}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <button onClick={submitQuiz} disabled={!allAnswered} style={{
        width: '100%', padding: '13px', borderRadius: 9, border: 'none', cursor: allAnswered ? 'pointer' : 'not-allowed',
        fontFamily: AF.b, fontSize: 14, fontWeight: 700,
        background: allAnswered ? 'var(--accent)' : C.surf2,
        color: allAnswered ? C.bg : C.dim,
      }}>
        {allAnswered ? 'Submit answers →' : `Answer all ${selectedText.questions.length} questions to submit`}
      </button>
    </div>
  );

  // ── RESULTS ───────────────────────────────────────────────────────────────
  if (view === "results") {
    const correct = answers.filter((a, i) => a === selectedText.questions[i].correctIndex).length;
    const pct = Math.round((correct / selectedText.questions.length) * 100);
    const R = 48, C2PI = 2 * Math.PI * R;
    const scoreColor = pct >= 80 ? C.green : pct >= 50 ? 'var(--warn)' : C.red;

    return (
      <div style={{ maxWidth: 640 }}>
        {/* Score ring */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ position: 'relative', width: 120, height: 120, margin: '0 auto 20px' }}>
            <svg width="120" height="120" viewBox="0 0 120 120">
              <circle cx="60" cy="60" r={R} fill="none" stroke={C.surf2} strokeWidth="7"/>
              <circle cx="60" cy="60" r={R} fill="none" stroke={scoreColor} strokeWidth="7"
                strokeDasharray={C2PI} strokeDashoffset={C2PI * (1 - pct / 100)}
                strokeLinecap="round" transform="rotate(-90 60 60)"
                style={{ transition: 'stroke-dashoffset 1s ease 0.2s' }}
              />
            </svg>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontFamily: AF.h, fontSize: 26, fontWeight: 700, color: scoreColor, letterSpacing: '-1px', lineHeight: 1 }}>{pct}%</span>
              <span style={{ fontFamily: AF.m, fontSize: 11, color: C.muted }}>{correct}/{selectedText.questions.length}</span>
            </div>
          </div>
          <div style={{ fontFamily: AF.h, fontSize: 22, fontWeight: 700, color: C.text, letterSpacing: '-0.6px', marginBottom: 6 }}>
            {pct === 100 ? 'Perfect.' : pct >= 80 ? 'Well done.' : pct >= 50 ? 'Good effort.' : 'Keep at it.'}
          </div>
          <div style={{ fontFamily: AF.b, fontSize: 14, color: C.muted }}>
            {pct < 50 ? 'Try re-reading the passage carefully before trying again.' :
             pct < 100 ? 'Re-read the passage to check the ones you missed.' :
             'Excellent reading comprehension.'}
          </div>
        </div>

        {/* Answer review */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
          {selectedText.questions.map((q, qi) => {
            const userAnswer = answers[qi];
            const isCorrect = userAnswer === q.correctIndex;
            return (
              <div key={qi} style={{
                padding: '16px 18px', borderRadius: 10,
                border: `1px solid ${isCorrect ? C.greenBorder : C.redBorder}`,
                background: isCorrect ? C.greenBg : C.redBg,
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
                  <span style={{ flexShrink: 0 }}>{isCorrect ? '✓' : '✗'}</span>
                  <p style={{ fontFamily: AF.b, fontSize: 14, fontWeight: 600, color: C.text, margin: 0 }}>{q.question}</p>
                </div>
                {!isCorrect && userAnswer !== null && (
                  <p style={{ fontFamily: AF.b, fontSize: 12, color: C.red, marginLeft: 22, marginBottom: 4 }}>
                    Your answer: {q.options[userAnswer]}
                  </p>
                )}
                <p style={{ fontFamily: AF.b, fontSize: 12, fontWeight: 600, color: C.green, marginLeft: 22, margin: 0 }}>
                  ✓ {q.options[q.correctIndex]}
                </p>
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => openText(selectedText)} style={{
            flex: 1, padding: '12px', borderRadius: 8,
            border: `1px solid ${C.border}`, background: 'transparent',
            fontFamily: AF.b, fontSize: 14, fontWeight: 500, color: C.muted, cursor: 'pointer',
          }}>
            Try again
          </button>
          <button onClick={backToList} style={{
            flex: 1, padding: '12px', borderRadius: 8, border: 'none',
            background: 'var(--accent)', color: C.bg,
            fontFamily: AF.b, fontSize: 14, fontWeight: 700, cursor: 'pointer',
          }}>
            All passages
          </button>
        </div>
      </div>
    );
  }

  return null;
}
